import React from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import DropdownIcon, {
	DropdownIconStyle,
} from '~/components/patterns/icons/DropdownIcon';
import Hint, {
	HintAttachment,
} from '~/components/patterns/hints/hint/Hint';
import StatusFlag, {
	StatusFlagStatus,
} from '~/components/patterns/statuses/StatusFlag';
import Website from '~/components/patterns/websites/Website';
import WebsiteFavicon, {
	WebsiteFaviconUsageContext,
} from '~/components/logic/websites/WebsiteFavicon';

import useHasMultipleAccounts from '~/hooks/useHasMultipleAccounts';
import useWebsiteDisplayName from '~/hooks/useWebsiteDisplayName';
import useWebsiteId from '~/hooks/useWebsiteId';
import useWebsiteIsCrawlingPaused from '~/hooks/useWebsiteIsCrawlingPaused';
import useWebsiteIsDiscovered from '~/hooks/useWebsiteIsDiscovered';
import useNavigationLayout from '~/hooks/useNavigationLayout';
import useEffectiveHomeAccountId from '~/hooks/useEffectiveHomeAccountId';

import {
	menu,
} from '~/actions';

import {
	isWebsiteSwitcherOpenSelector,
} from '~/state/ui/menu/selectors';

import {
	useSelectedAccount,
} from '~/contexts/SelectedAccountContext';



const messages = defineMessages({
	progressDescription: {
		id: 'ui.menu.progress.tooltip',
	},
});



type Props = {
	accountName: string | null,
	isTechnicalExpanded?: boolean,
};

const WebsitesNavToggle: React.FC<Props> = (props) => {
	const {
		accountName,
		isTechnicalExpanded = true,
	} = props;

	const websiteId = useWebsiteId();

	const hasMultipleAccounts = useHasMultipleAccounts();
	const open = useSelector(isWebsiteSwitcherOpenSelector);
	const websiteIsCrawlingPaused = useWebsiteIsCrawlingPaused(websiteId);
	const websiteIsDiscovered = useWebsiteIsDiscovered(websiteId);

	const websiteDisplayName = useWebsiteDisplayName(websiteId);

	const dispatch = useDispatch();

	// Check if we're in new navigation
	const [
		currentLayout,
	] = useNavigationLayout();
	const isNewNavigation = currentLayout === 'new';

	// Get selectedAccountId from context (for new navigation inline section toggle)
	const {
		selectedAccountId,
		setSelectedAccountId,
	} = useSelectedAccount();
	const effectiveHomeAccountId = useEffectiveHomeAccountId();

	// Determine if inline section is visible (new navigation only)
	// Must match InlineWebsitesSection render condition: selectedAccountId && isTechnicalExpanded && isWebsiteSwitcherOpen
	const isInlineSectionVisible = isNewNavigation && selectedAccountId !== null && open;

	const handleToggleSelect = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();

			// For new navigation: toggle inline section by setting/clearing selectedAccountId
			if (isNewNavigation) {
				if (isInlineSectionVisible) {
					// Inline section is visible - close it (keep selectedAccountId for next open)
					dispatch(menu.closeWebsiteSwitcher());
				} else {
					// Inline section is not visible - open it
					// Preserve selectedAccountId if already set (e.g. from Accounts list selection)
					setSelectedAccountId(selectedAccountId ?? effectiveHomeAccountId);
					dispatch(menu.openWebsiteSwitcher());
				}
			} else {
				// Old navigation: use existing Redux toggle behavior
				if (open) {
					dispatch(menu.closeWebsiteSwitcher());
				} else {
					dispatch(menu.openWebsiteSwitcher());
				}
			}
		},
		[
			dispatch,
			open,
			isNewNavigation,
			isInlineSectionVisible,
			selectedAccountId,
			setSelectedAccountId,
			effectiveHomeAccountId,
		],
	);

	function renderStatusFlag() {
		let iconStatus: StatusFlagStatus | null = null;

		if (websiteIsCrawlingPaused) {
			iconStatus = StatusFlagStatus.Paused;
		} else if (websiteIsDiscovered === false) {
			iconStatus = StatusFlagStatus.DiscoveryInProgress;
		}

		if (iconStatus === null) {
			return null;
		}

		if (iconStatus === StatusFlagStatus.DiscoveryInProgress) {
			return (
				<Hint
					attachment={HintAttachment.Left}
					popup={(
						<FormattedMessage {...messages.progressDescription} />
					)}
					popupMaxWidth={260}
					popupZIndex={1500}
				>
					<StatusFlag status={iconStatus} />
				</Hint>
			);
		}

		return (
			<StatusFlag status={iconStatus} />
		);
	}

	// For new navigation: never highlight the trigger when expanded (designer: gray when clicked to avoid confusion)
	// For old navigation: use Redux open state for highlighting
	const isHighlighted = isNewNavigation ? false : open;
	const isDropdownIconActive = isNewNavigation ? isInlineSectionVisible : open;

	// For new navigation, don't show account name label
	// For old navigation, show account name if user has multiple accounts
	const displayAccountName = isNewNavigation
		? undefined
		: (hasMultipleAccounts && accountName ? accountName : undefined);

	return (
		<Website
			accountName={displayAccountName}
			additionalIcon={(
				<DropdownIcon
					isActive={isDropdownIconActive}
					size={10}
					style={DropdownIconStyle.White}
				/>
			)}
			alwaysShowHoverBackground={isNewNavigation && isTechnicalExpanded && !isInlineSectionVisible}
			favicon={(
				<WebsiteFavicon
					usageContext={WebsiteFaviconUsageContext.Sidebar}
					websiteId={websiteId}
				/>
			)}
			isHighlighted={isHighlighted}
			isMain={true}
			name={websiteDisplayName}
			onClick={handleToggleSelect}
			statusFlag={renderStatusFlag()}
		/>
	);
};



export default React.memo(WebsitesNavToggle);
